import React, { useState } from 'react'
import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react'
import './InstagramPreview.css'

interface InstagramPreviewProps {
  caption: string
  images: string[]
  hashtags: string[]
  accountName?: string
  accountAvatar?: string
}

export default function InstagramPreview({
  caption,
  images,
  hashtags,
  accountName = 'fixmybrick',
  accountAvatar
}: InstagramPreviewProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  
  // Process caption to highlight hashtags and mentions
  const processCaption = (text: string) => {
    // First, add hashtags to the caption if they're not already included
    let fullCaption = text
    const captionHashtags = text.match(/#\w+/g) || []
    const captionHashtagsLower = captionHashtags.map(h => h.toLowerCase())
    
    // Add missing hashtags to the end
    const hashtagsToAdd = hashtags.filter(h => !captionHashtagsLower.includes(h.toLowerCase()))
    if (hashtagsToAdd.length > 0) {
      fullCaption = text + (text.trim() ? '\n\n' : '') + hashtagsToAdd.join(' ')
    }
    
    // Replace hashtags and mentions with styled spans
    const parts = fullCaption.split(/(#\w+|@\w+)/g)
    
    return parts.map((part, index) => {
      if (part.startsWith('#') || part.startsWith('@')) {
        return (
          <span key={index} className="instagram-link">
            {part}
          </span>
        )
      }
      // Convert line breaks to <br> tags
      return part.split('\n').map((line, lineIndex) => (
        <React.Fragment key={`${index}-${lineIndex}`}>
          {lineIndex > 0 && <br />}
          {line}
        </React.Fragment>
      ))
    })
  }
  
  // Truncate caption for preview
  const shouldTruncate = caption.length > 125
  const displayCaption = shouldTruncate ? caption.substring(0, 125) : caption
  
  return (
    <div className="instagram-preview">
      {/* Post Header */}
      <div className="instagram-header">
        <div className="instagram-header-left">
          <div className="instagram-avatar">
            {accountAvatar ? (
              <img src={accountAvatar} alt={accountName} className="instagram-avatar-image" />
            ) : (
              <div className="instagram-avatar-fallback">
                {accountName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <span className="instagram-username">{accountName}</span>
        </div>
        <button className="instagram-more-button">
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </div>
      
      {/* Post Images */}
      <div className="instagram-image-container">
        {images.length > 0 ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={`Post image ${currentImageIndex + 1}`}
              className="instagram-image"
            />
            {images.length > 1 && (
              <div className="instagram-dots">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`instagram-dot ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="instagram-image-placeholder">
            <div className="instagram-placeholder-content">
              <div className="instagram-placeholder-icon">📷</div>
              <p>No image added yet</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      <div className="instagram-actions">
        <div className="instagram-actions-left">
          <button className="instagram-action-button">
            <Heart className="h-6 w-6" />
          </button>
          <button className="instagram-action-button">
            <MessageCircle className="h-6 w-6" />
          </button>
          <button className="instagram-action-button">
            <Send className="h-6 w-6" />
          </button>
        </div>
        <button className="instagram-action-button">
          <Bookmark className="h-6 w-6" />
        </button>
      </div>
      
      {/* Caption */}
      <div className="instagram-caption-section">
        <div className="instagram-caption">
          <span className="instagram-username">{accountName}</span>{' '}
          <span className="instagram-caption-text">
            {processCaption(displayCaption)}
            {shouldTruncate && (
              <span className="instagram-more">... more</span>
            )}
          </span>
        </div>
        
        {/* Timestamp */}
        <div className="instagram-timestamp">Just now</div>
      </div>
    </div>
  )
}